// @ts-nocheck
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { rem } from 'polished';
import { useTranslation } from 'react-i18next';
import Grid from '@material-ui/core/Grid';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import { Modal, Radio, Textarea, Spinner, Box } from 'components';
import { PoHeaderExtract } from './po-header-extract';
import {
  ModalCancelButton,
  ModalRejectButton,
  ModalCloseButton,
  successModalPaperProps
} from './delivery-modal-actions';

const Label = styled.span<{ error?: boolean }>`
  color: ${props => (props.error ? props.theme.colors.Ma[400] : '#1e293b')};
  font-size: ${rem('16px')};
`;

const ReasonLabel = styled(FormControlLabel)`
  margin-left: 0;
  margin-right: 0;

  .MuiFormControlLabel-label {
    color: #1e293b;
    font-size: ${rem('16px')};
  }
`;

const ErrorMessage = styled(Label)`
  flex: 1;
  padding-left: ${rem('8px')};
`;

function ReasonsSection({ onChange, selected, error, reasons = [] }) {
  const { t } = useTranslation();

  return (
    <Box paddingY="8px">
      <Label error={error}>{t('summary:modals.reject.reason_section_title')}</Label>
      <RadioGroup value={selected ?? ''} onChange={onChange}>
        <Grid container>
          {(reasons || []).map(reason => (
            <Grid item xs={6} key={reason.value}>
              <ReasonLabel
                value={reason.value}
                control={<Radio color="default" />}
                label={t(reason.label)}
              />
            </Grid>
          ))}
        </Grid>
      </RadioGroup>
    </Box>
  );
}

function TextAreaSection({ onChange }) {
  const { t } = useTranslation();

  return (
    <Box key="textarea" paddingY="8px">
      <Textarea
        key="textarea"
        fullWidth
        placeholder={t('summary:modals.reject.textarea_placeholder')}
        label={t('summary:modals.reject.textarea_placeholder')}
        multiline
        rows={6}
        rowsMax={6}
        onChange={onChange}
      />
    </Box>
  );
}

const useValue = initialValue => {
  const [value, setValue] = useState(initialValue);
  const onChange = event => setValue(event.target.value);
  const clear = () => setValue(initialValue);

  return {
    value,
    onChange,
    clear
  };
};

export function RejectModal({ visible, onClose, onReject, extract, loading, success, reasons }) {
  const { t } = useTranslation();
  const comment = useValue('');
  const reason = useValue('');
  const [error, setError] = useState(false);

  const moderationReasons = (reasons || []).map(reasonItem => ({
    label: 'summary:modals.reject.reasons.' + reasonItem.name,
    // RadioGroup always emits string values; keep ids as strings so selection sticks.
    value: String(reasonItem.id)
  }));

  const clearState = () => {
    reason.clear();
    comment.clear();
    setError(false);
  };

  const onClickReject = () => {
    const { value: reasonVal } = reason;

    if (reasonVal) {
      const payload = { comment: comment.value, reason: parseInt(reasonVal, 10) };
      clearState();
      onReject(payload);
    } else {
      setError(true);
    }
  };

  const onClickClose = () => {
    clearState();
    onClose();
  };

  useEffect(() => {
    setError(false);
  }, [comment.value, reason.value]);

  return (
    <Modal
      fullWidth
      maxWidth="md"
      open={visible}
      onClose={onClickClose}
      loading={loading}
      LoadingComponent={<Spinner label={t('summary:modals.reject.loading_message')} />}
      PaperProps={success ? successModalPaperProps : undefined}
    >
      <Modal.Title>{t('summary:modals.reject.title')}</Modal.Title>
      <Modal.Content>
        {success ? (
          <Label>{t('summary:modals.reject.success.message', { po: extract[0].value })}</Label>
        ) : (
          <>
            <PoHeaderExtract extract={extract} />
            <ReasonsSection
              onChange={reason.onChange}
              selected={reason.value}
              error={error}
              reasons={moderationReasons}
            />
            <TextAreaSection onChange={comment.onChange} />
          </>
        )}
      </Modal.Content>
      <Modal.Actions>
        {success ? (
          <ModalCloseButton colorName={{}} onClick={onClose}>
            {t('summary:modals.close_button')}
          </ModalCloseButton>
        ) : (
          <>
            {error && (
              <ErrorMessage error>{t('summary:modals.reject.error.message')}</ErrorMessage>
            )}
            <ModalCancelButton colorName={{}} onClick={onClickClose}>
              {t('summary:modals.cancel_button')}
            </ModalCancelButton>
            <ModalRejectButton colorName={{}} onClick={onClickReject}>
              {t('summary:modals.reject.button')}
            </ModalRejectButton>
          </>
        )}
      </Modal.Actions>
    </Modal>
  );
}
